/**
 * ConsultationPreviewPage
 * -----------------------
 * The free payoff for having captured, shown before anything is bought.
 *
 * Two jobs:
 *   1. Show the client the points we actually detected on *their* face. The
 *      landmarks were computed on their own device during capture and stored
 *      with it, so drawing them costs nothing and runs no agent.
 *   2. Explain what each agent reads off those points, so the paid step stops
 *      being a black box.
 *
 * Then one large action: choose a service, pick a doctor, pay. Nothing on this
 * page spends a token.
 */
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Stack,
  Button,
  Chip,
  Alert,
  Skeleton,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Container from '@/components/PageSheet';
import AgentShowcase from '@/components/AgentShowcase';
import { consultationsApi } from '@/services/api';
import { useSelector } from 'react-redux';

/**
 * The capture and its landmark map, side by side.
 *
 * A field of dots on its own means little; held against the photo it came from,
 * the client can see their own face being read. Both panels share the same
 * aspect ratio and the same coordinate space, so features line up across the
 * pair.
 */
function LandmarkPair({ frame, t }) {
  if (!frame?.landmarks?.length) return null;
  const w = frame.imageSize?.width || 512;
  const h = frame.imageSize?.height || 512;

  const panel = {
    position: 'relative',
    flex: 1,
    minWidth: 0,
    aspectRatio: '1 / 1',
    borderRadius: 3,
    overflow: 'hidden',
    border: '1px solid rgba(192,167,134,.28)',
  };
  const caption = {
    position: 'absolute',
    insetInline: 0,
    bottom: 0,
    py: 0.5,
    textAlign: 'center',
    fontSize: '.65rem',
    letterSpacing: '.16em',
    textTransform: 'uppercase',
    fontWeight: 700,
    color: '#E8DCC8',
    background: 'linear-gradient(180deg, rgba(10,20,37,0), rgba(10,20,37,.85))',
  };

  return (
    <Stack direction="row" spacing={1}>
      <Box sx={{ ...panel, background: '#0A1425' }}>
        {frame.image ? (
          <Box component="img" src={frame.image} alt=""
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <Box sx={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center' }}>
            <Typography variant="caption" sx={{ color: '#8FA0B8' }}>
              {t('preview.photoUnavailable')}
            </Typography>
          </Box>
        )}
        <Typography component="span" sx={caption}>{t('preview.panelPhoto')}</Typography>
      </Box>

      <Box sx={{ ...panel, background: 'linear-gradient(160deg,#16294a 0%,#11223E 55%,#0A1425 100%)' }}>
        <svg viewBox={`0 0 ${w} ${h}`} width="100%" height="100%" aria-hidden="true">
          {frame.landmarks.map((pt, i) => (
            <circle key={i} cx={pt.x * w} cy={pt.y * h}
              r={Math.max(1.6, w / 260)} fill="#C0A786" fillOpacity="0.95" />
          ))}
        </svg>
        <Typography component="span" sx={caption}>{t('preview.panelPoints')}</Typography>
      </Box>
    </Stack>
  );
}

export default function ConsultationPreviewPage() {
  const { consultationId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isRtl = i18n.dir() === 'rtl';
  const Forward = isRtl ? ArrowBackIcon : ArrowForwardIcon;
  const [poseIdx, setPoseIdx] = useState(0);
  // A pre-signup guest can see everything here, but cannot buy: there is no
  // profile to attach an order and a medical report to yet.
  const user = useSelector((st) => st.auth.user);
  // Only claim someone needs to sign up once the server has actually said so.
  // `!user` alone is also true while the profile is still loading or when the
  // fetch failed, which is how registered clients were shown the signup form.
  const profileLoaded = useSelector((st) => st.auth.profileLoaded);
  const profileUnknown = !profileLoaded;
  const needsProfile = profileLoaded && !user?.firstName;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['consultation-preview', consultationId],
    queryFn: () => consultationsApi.getPreview(consultationId),
  });

  const frames = data?.frames || [];
  const frame = frames[Math.min(poseIdx, Math.max(frames.length - 1, 0))];
  const agents = data?.agents || [];

  if (isLoading) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={40} />
          <Skeleton variant="rounded" height={320} />
          <Skeleton variant="rounded" height={160} />
        </Stack>
      </Container>
    );
  }

  if (isError || !data) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
        <Alert severity="error">{t('common.error')}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      {/* The action lives at the TOP and stays put: the page below is a long
          scroll of landmark art and agent explainers, and the way forward
          should never be something you have to hunt for at the bottom of it.
          `top` matches the sticky AppBar height so it parks directly beneath. */}
      <Box
        sx={{
          position: 'sticky',
          top: { xs: 56, sm: 64 },
          zIndex: 5,
          // bleed to the sheet's edges so nothing shows through behind it
          mx: { xs: -2, sm: -3 },
          px: { xs: 2, sm: 3 },
          pt: 1,
          pb: 1.5,
          mb: 2,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button
          fullWidth
          size="large"
          variant="contained"
          endIcon={<Forward />}
          disabled={profileUnknown}
          onClick={() =>
            navigate(
              needsProfile
                ? `/signup?next=${encodeURIComponent(
                    `/consultations/${consultationId}/choose-physician`)}`
                : `/consultations/${encodeURIComponent(consultationId)}/choose-physician`,
            )
          }
          sx={{ py: 1.5, fontSize: '1rem', fontWeight: 800, borderRadius: 3 }}
        >
          {profileUnknown
            ? t('common.loading')
            : needsProfile ? t('preview.ctaCompleteProfile') : t('preview.cta')}
        </Button>

        {/* Said here because this is where a client learns their photos exist
            and are temporary — the only moment keeping them is obviously worth
            something. Quiet, below the primary action: it must not compete
            with finishing the consultation. */}
        <Typography variant="caption" color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}>
          {t('preview.retentionNote')}{' '}
          <Box component="span" role="button" tabIndex={0}
            onClick={() => navigate('/candle-card')}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/candle-card')}
            sx={{ color: 'secondary.dark', fontWeight: 700, cursor: 'pointer',
              textDecoration: 'underline' }}>
            {t('preview.retentionLink')}
          </Box>
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          align="center"
          sx={{ display: 'block', mt: 0.75 }}
        >
          {needsProfile ? t('preview.ctaCompleteProfileHint') : t('preview.ctaHint')}
        </Typography>
      </Box>

      <Typography variant="h6" fontWeight={800}>
        {t('preview.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('preview.subtitle', { count: data.landmarkCount || 0 })}
      </Typography>

      {/* 1 — what we detected on their own face */}
      {frame ? (
        <>
          <LandmarkPair frame={frame} t={t} />
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 1.5, mb: 1 }}
          >
            <Chip
              size="small"
              color="secondary"
              label={t('preview.pointsChip', { count: frame.count })}
            />
            {frames.length > 1 && (
              <ToggleButtonGroup
                size="small"
                exclusive
                value={poseIdx}
                onChange={(_, v) => v != null && setPoseIdx(v)}
              >
                {frames.map((f, i) => (
                  <ToggleButton key={i} value={i} sx={{ px: 1.25 }}>
                    {i + 1}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
          </Stack>
        </>
      ) : (
        <Alert severity="info" sx={{ borderRadius: 3 }}>{t('preview.noFrames')}</Alert>
      )}

      <Divider sx={{ my: 3 }} />

      {/* 2 — what the agents read off those points */}
      <Typography
        sx={{
          fontSize: '.65rem', letterSpacing: '.28em', textTransform: 'uppercase',
          color: 'secondary.dark', fontWeight: 700,
        }}
      >
        {t('preview.howKicker')}
      </Typography>
      <Typography variant="h6" fontWeight={800} sx={{ mt: 0.5 }}>
        {t('preview.howTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, maxWidth: '46ch' }}>
        {t('preview.howSubtitle')}
      </Typography>

      <Stack spacing={2}>
        {agents.map((a) => (
          <AgentShowcase key={a.key} agent={a} lang={lang} t={t} />
        ))}
      </Stack>

    </Container>
  );
}
